import { Mapper } from 'modules/cm/mapper/types';
import { UnderwritingSummary } from 'modules/cm/api/cases/underwiting/types';
import {
  RepaymentStrategyField,
  Affordability,
  Approvals,
  Assets,
  Borrower,
  CreditRecord,
  Expenditure,
  Income,
  Loan,
  MortgageProperty,
  RepaymentStrategy,
  SpecialConditions,
  SUBDOMAINS,
  Summary,
  Underwriting,
} from 'modules/cm/domain/Underwriting/Underwriting';
import { CaseDetails } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { getResource } from 'modules/cm/utils/resources';
import { ArgumentsOf } from 'utils/types';
import { FIELDS } from 'modules/cm/domain/Underwriting/Underwriting';

type UnderwritingSummaryApplicant = NonNullable<UnderwritingSummary['applicant1' | 'applicant2']>;
type Resources = CaseDetails['resources'];

const getResourceValue = (...args: ArgumentsOf<typeof getResource>) => {
  const resource = getResource(...args);
  return resource ? resource.option : null;
};

const mapSummary = (api: UnderwritingSummary, resources: Resources): Summary => {
  return {
    [FIELDS.APPLICATION_DATE]: api.dateSubmitted,
    [FIELDS.APPLICATION_ID]: api.caseId,
    [FIELDS.APPLICANT_SURNAME]: api.applicant1.lastName,
    [FIELDS.CREDIT_POLICY_EXCEPTION_RATIONALE]: api.creditPolicyExemptionRationale,
    [FIELDS.DELEGATED_AUTHORITY_LEVEL]: getResourceValue(resources.authorityLevels, api.delegatedAuthorityLevel),
    [FIELDS.FIRM_NAME]: api.firmName,
    [FIELDS.HAS_ADVERSE_CREDIT]: api.hasAdverseCredit,
    [FIELDS.IS_CREDIT_POLICY_EXEMPTION]: api.isCreditPolicyExemption,
    [FIELDS.UNDERWRITER]: api.underwriterFullName,
    [FIELDS.UNDERWRITER_RECOMMENDATION]: api.underwriterRecommendation,
  };
};

const mapApprovals = (api: UnderwritingSummary, resources: Resources): Approvals => {
  return {
    [FIELDS.APPROVED_BY]: getResourceValue(resources.authorityLevels, api.approvalBy),
    [FIELDS.APPROVED_DATE]: api.approvalDate,
    [FIELDS.INITIAL_ASSESSMENT_BY]:
      typeof api.initialAssessmentBy === 'number'
        ? getResourceValue(resources.underwriters, api.initialAssessmentBy.toString())
        : null,
    [FIELDS.INITIAL_ASSESSMENT_DATE]: api.initialAssessmentDate,
  };
};

const mapAssets = (api: UnderwritingSummary, resources: Resources): Assets => {
  const mapAsset = (asset: UnderwritingSummaryApplicant['assets'][0]): Assets['applicant1'][0] => {
    return {
      ...asset,
      [FIELDS.ASSET_TYPE]: getResourceValue(resources.assetTypes, asset.type),
    };
  };
  return {
    [FIELDS.APPLICANT_1]: api.applicant1.assets.map(mapAsset),
    [FIELDS.APPLICANT_2]: api.applicant2 ? api.applicant2.assets.map(mapAsset) : [],
  };
};

const mapBorrower = (api: UnderwritingSummary, resources: Resources): Borrower => {
  const mapApplicant = (api: UnderwritingSummaryApplicant): Borrower['applicant1'] => {
    return {
      [FIELDS.AGE]: api.age,
      [FIELDS.EMPLOYMENT_STATUS]: getResourceValue(resources.employmentStatuses, api.employmentStatus),
      [FIELDS.FIRST_NAME]: api.firstName,
      [FIELDS.GENDER]: getResourceValue(resources.gender, api.gender),
      [FIELDS.LAST_NAME]: api.lastName,
      [FIELDS.MARITAL_STATUS]: getResourceValue(resources.martialStatus, api.maritalStatus),
      [FIELDS.NATIONALITY]: api.nationality,
      [FIELDS.PLANNED_RETIREMENT_AGE]: api.plannedRetirementAge,
      [FIELDS.POWER_OF_ATTORNEY_FULL_NAME]: api.powerOfAttorneyFullName,
      [FIELDS.PROOF_OF_ADDRESS]: getResourceValue(resources.proofOfAddress, api.proofOfAddress),
      [FIELDS.PROOF_OF_ID]: getResourceValue(resources.proofOfId, api.proofOfId),
    };
  };

  return {
    [FIELDS.APPLICANT_1]: mapApplicant(api.applicant1),
    [FIELDS.APPLICANT_2]: api.applicant2 ? mapApplicant(api.applicant2) : null,
  };
};

const mapCreditRecord = (api: UnderwritingSummary, resources: Resources): CreditRecord => {
  const mapApplicant = (api: UnderwritingSummaryApplicant): CreditRecord['applicant1'] => {
    return {
      [FIELDS.ADVERSE_CREDIT_DETAILS]: api.adverseCreditDetails,
      [FIELDS.BANKRUPTCY_AMOUNT]: api.bankruptcyAmount,
      [FIELDS.BANKRUPTCY_LATEST_DATE]: api.bankruptcyLatestDate,
      [FIELDS.CCJ_AMOUNT]: api.ccjAmount,
      [FIELDS.CCJ_LATEST_DATE]: api.ccjLatestDate,
      [FIELDS.CRIMINAL_CONVICTIONS_DETAILS]: api.criminalConvictionsDetails,
      [FIELDS.DEFAULT_AMOUNT]: api.defaultAmount,
      [FIELDS.DEFAULT_LATEST_DATE]: api.defaultLatestDate,
      [FIELDS.HAS_ADVERSE_CREDIT]: api.hasAdverseCredit,
      [FIELDS.HAS_CRIMINAL_CONVICTIONS]: api.hasCriminalConvictions,
      [FIELDS.MORTGAGE_ARREARS_AMOUNT]: api.mortgageArrearsAmount,
      [FIELDS.MORTGAGE_ARREARS_LATEST_DATE]: api.mortgageArrearsLatestDate,
    };
  };

  return {
    [FIELDS.APPLICANT_1]: mapApplicant(api.applicant1),
    [FIELDS.APPLICANT_2]: api.applicant2 ? mapApplicant(api.applicant2) : null,
    [FIELDS.DELPHI_SCORE]: api.delphiScore,
  };
};

const mapExpenditure = (api: UnderwritingSummary, resources: Resources): Expenditure => {
  const mapApplicant = (applicantApi: UnderwritingSummaryApplicant): Expenditure['applicant1'] => {
    return {
      [FIELDS.COMMITMENT_BALANCE]: {
        [FIELDS.COMMITMENT_REMAINING]: applicantApi.commitmentsRemainingBalance,
        [FIELDS.COMMITMENT_TO_BE_CLEARED]: applicantApi.commitmentsToBeClearedBalance,
        [FIELDS.COMMITMENT_TOTAL]: applicantApi.expenditureTotalBalance,
      },
      [FIELDS.COMMITMENT_MONTHLY]: {
        [FIELDS.COMMITMENT_REMAINING]: applicantApi.commitmentsRemainingMonthly,
        [FIELDS.COMMITMENT_TO_BE_CLEARED]: applicantApi.commitmentsToBeClearedMonthly,
        [FIELDS.COMMITMENT_TOTAL]: applicantApi.expenditureTotalMonthly,
      },
    };
  };

  return {
    [FIELDS.APPLICANT_1]: mapApplicant(api.applicant1),
    [FIELDS.APPLICANT_2]: api.applicant2 ? mapApplicant(api.applicant2) : null,
    [FIELDS.COMMITMENT_COMBINED_MONTHLY_BASIC_SPEND]: api.combinedMonthlyBasicSpend,
  };
};

const mapIncome = (api: UnderwritingSummary, resources: Resources): Income => {
  const mapApplicantCurrentIncome = (applicantApi: UnderwritingSummaryApplicant): Income['current']['applicant1'] => {
    return {
      [FIELDS.EMPLOYMENT_INCOME]: applicantApi.currentIncomeEmploymentIncome,
      [FIELDS.OTHER_INCOME]: applicantApi.currentIncomeOtherIncome,
      [FIELDS.PENSION_INCOME]: applicantApi.currentIncomePensionIncome,
      [FIELDS.SELF_EMPLOYMENT_INCOME]: applicantApi.currentIncomeSelfEmploymentIncome,
      [FIELDS.TOTAL_INCOME]: applicantApi.currentIncomeTotalIncome,
    };
  };

  const mapApplicantFutureIncome = (applicantApi: UnderwritingSummaryApplicant): Income['future']['applicant1'] => {
    return {
      [FIELDS.EMPLOYMENT_INCOME]: applicantApi.futureIncomeEmploymentIncome,
      [FIELDS.OTHER_INCOME]: applicantApi.futureIncomeOtherIncome,
      [FIELDS.PENSION_INCOME]: applicantApi.futureIncomePensionIncome,
      [FIELDS.SELF_EMPLOYMENT_INCOME]: applicantApi.futureIncomeSelfEmploymentIncome,
      [FIELDS.TOTAL_INCOME]: applicantApi.futureIncomeTotalIncome,
    };
  };

  const mapApplicantSurvivorIncome = (
    applicantApi: UnderwritingSummaryApplicant,
  ): Income[FIELDS.SURVIVOR]['applicant1'] => {
    return {
      survivorIncome: applicantApi.survivorIncome,
    };
  };

  return {
    [FIELDS.CURRENT_INCOME]: {
      [FIELDS.APPLICANT_1]: mapApplicantCurrentIncome(api.applicant1),
      [FIELDS.APPLICANT_2]: api.applicant2 ? mapApplicantCurrentIncome(api.applicant2) : null,
      [FIELDS.COMBINED_INCOME]: api.currentIncomeCombined,
    },
    [FIELDS.FUTURE_INCOME]: {
      [FIELDS.APPLICANT_1]: mapApplicantFutureIncome(api.applicant1),
      [FIELDS.APPLICANT_2]: api.applicant2 ? mapApplicantFutureIncome(api.applicant2) : null,
      [FIELDS.COMBINED_INCOME]: api.futureIncomeCombined,
    },
    [FIELDS.SURVIVOR]: {
      applicant1: mapApplicantSurvivorIncome(api.applicant1),
      applicant2: api.applicant2 ? mapApplicantSurvivorIncome(api.applicant2) : null,
    },
  };
};

const mapLoan = (api: UnderwritingSummary, resources: Resources): Loan => {
  return {
    [FIELDS.ASSISTING_OTHERS]: api.loanAssistingOthers,
    [FIELDS.DEBT_CONSOLIDATION]: api.loanDebtConsolidation,
    [FIELDS.DEBT_TO_INCOME]: api.debtToIncome,
    [FIELDS.DISCRETIONARY_SPEND]: api.loanDiscretionarySpend,
    [FIELDS.FIXED_INTEREST_RATE]: api.fixedInterestRate,
    [FIELDS.GROSS_LOAN]: api.grossLoan,
    [FIELDS.NET_LOAN]: api.netLoan,
    [FIELDS.HOME_IMPROVEMENTS]: api.loanHomeImprovements,
    [FIELDS.LOAN_PURCHASE]: api.loanAmountPurchase,
    [FIELDS.LOAN_PURPOSE]: getResourceValue(resources.purposesOfTheLoan, api.purposeOfTheLoan),
    [FIELDS.LOAN_REMORTGAGE]: api.loanAmountRemortgage,
    [FIELDS.LOAN_TO_VALUE]: api.loanToValue,
    [FIELDS.OTHER_PURPOSES]: api.loanOtherPurposes,
    [FIELDS.PRODUCT_NAME]: api.productName,
    [FIELDS.PRODUCT_TERM_LENGTH]: api.productTermLength,
    [FIELDS.PRODUCT_TYPE]: api.productType,
  };
};

const mapMortgageProperty = (api: UnderwritingSummary, resources: Resources): MortgageProperty => {
  const { mortgagedProperty } = api;
  return {
    [FIELDS.CITY]: mortgagedProperty.city,
    [FIELDS.FLAT_NUMBER]: mortgagedProperty.flatNumber,
    [FIELDS.BUILDING_NUMBER]: mortgagedProperty.buildingNumber,
    [FIELDS.BUILDING_NAME]: mortgagedProperty.buildingName,
    [FIELDS.STREET]: mortgagedProperty.street,
    [FIELDS.POSTCODE]: mortgagedProperty.postcode,
    [FIELDS.REGION]: mortgagedProperty.region,
    [FIELDS.REMAINING_LEASE_YEARS]: mortgagedProperty.remainingLeaseYears,
    [FIELDS.TENURE]: getResourceValue(resources.tenureTypes, mortgagedProperty.propertyTenure),
    [FIELDS.TYPE]: getResourceValue(resources.propertyTypes, mortgagedProperty.type),
    [FIELDS.VALUATION_CONFIRMED_VALUE]: mortgagedProperty.confirmedValuation,
    [FIELDS.VALUATION_ESTIMATED_VALUE]: mortgagedProperty.valuationEstimate,
    [FIELDS.VALUATION_INSPECTION_DATE]: mortgagedProperty.inspectedAt,
    [FIELDS.VALUATION_TYPE]: getResourceValue(resources.valuationTypes, mortgagedProperty.valuationType),
  };
};

const mapSpecialConditions = (api: UnderwritingSummary, resources: Resources): SpecialConditions => {
  return {
    [FIELDS.SPECIAL_CONDITIONS]: api.underwriterSpecialConditions,
  };
};

const mapAffordability = (api: UnderwritingSummary, resources: Resources): Affordability => {
  return {
    [FIELDS.DISPOSABLE_INCOME]: api.afDisposableIncome ? Number(api.afDisposableIncome) : null,
    [FIELDS.DISPOSABLE_INCOME_BUFFER]: api.afDisposableIncomeBuffer ? Number(api.afDisposableIncomeBuffer) : null,
    [FIELDS.LIVING_COSTS]: api.afLivingCosts ? Number(api.afLivingCosts) : null,
    [FIELDS.MAXIMUM_LOAN_POSSIBLE]: api.afMaxLoanPossible,
    [FIELDS.NET_CASHFLOW]: api.afNetCashflow ? Number(api.afNetCashflow) : null,
    [FIELDS.STAGE]: api.afStage,
  };
};

const mapRepaymentStrategy = (uwApi: UnderwritingSummary): RepaymentStrategy => {
  const api = uwApi.repaymentStrategy;

  return {
    [RepaymentStrategyField.HaveEndownments]: api.hasEndowments,
    [RepaymentStrategyField.HaveInvestments]: api.hasInvestments,
    [RepaymentStrategyField.HaveSaleOfAnotherProperty]: api.hasAnotherPropertyToSale,
    [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: api.hasPropertyToSaleForDownsizing,
    [RepaymentStrategyField.HaveOther]: api.hasOtherRepayments,
  };
};

type UWDataMapper = Omit<
  Mapper<UnderwritingSummary, Underwriting>,
  'mapApiToDomain' | 'mapDomainFieldToAPI' | 'mapDomainNameToAPI'
> & {
  mapApiToDomain: (api: UnderwritingSummary, resources: Resources) => Underwriting;
};

const mapper: UWDataMapper = {
  mapApiToDomain: (api: UnderwritingSummary, resources: Resources): Underwriting => {
    return {
      [SUBDOMAINS.SUMMARY]: mapSummary(api, resources),
      [SUBDOMAINS.APPROVALS]: mapApprovals(api, resources),
      [SUBDOMAINS.ASSETS]: mapAssets(api, resources),
      [SUBDOMAINS.BORROWER]: mapBorrower(api, resources),
      [SUBDOMAINS.CREDIT_RECORD]: mapCreditRecord(api, resources),
      [SUBDOMAINS.EXPENDITURE]: mapExpenditure(api, resources),
      [SUBDOMAINS.INCOME]: mapIncome(api, resources),
      [SUBDOMAINS.LOAN]: mapLoan(api, resources),
      [SUBDOMAINS.MORTGAGE_PROPERTY]: mapMortgageProperty(api, resources),
      [SUBDOMAINS.SPECIAL_CONDITIONS]: mapSpecialConditions(api, resources),
      [SUBDOMAINS.AFFORDABILITY]: mapAffordability(api, resources),
      [SUBDOMAINS.REPAYMENT_STRATEGY]: mapRepaymentStrategy(api),
    };
  },
};

export default mapper;
